import { useQuery } from '@tanstack/react-query'
import { BellIcon } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'

import { notificationsApi } from '../services/notificationsApi'
import { useAuthStore } from '../store'
import { NotificationDropdown } from './NotificationDropdown'

type NotificationBellProps = {
  portal?: string
}

export function NotificationBell({ portal }: NotificationBellProps) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const activeOrgId = useAuthStore((s) => s.user?.organization_id)

  const { data } = useQuery({
    queryKey: ['notifications-unread-count', activeOrgId, portal],
    queryFn: () => notificationsApi.getUnreadCount(portal),
    refetchInterval: 30_000,
    staleTime: 15_000,
  })

  const unreadCount = data?.unread_count ?? 0

  // Click outside
  useEffect(() => {
    if (!isOpen) return
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [isOpen])

  // Escape
  useEffect(() => {
    if (!isOpen) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [isOpen])

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={`Notificacoes${unreadCount > 0 ? ` (${unreadCount} não lidas)` : ''}`}
        className="relative rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
      >
        <BellIcon className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      <NotificationDropdown isOpen={isOpen} onClose={() => setIsOpen(false)} portal={portal} />
    </div>
  )
}
